import React, { useEffect, useState, useCallback } from "react";
import { CreditCard, Plus, Edit2, Trash2, X, Check } from "lucide-react";
import { toast } from "sonner";

const newId = () => `m_${Math.random().toString(36).slice(2, 10)}`;
const VACIO = { nombre: "", cuenta_id: "", activa: true, metodos: [{ id: newId(), nombre: "Débito", pct: "" }, { id: newId(), nombre: "Crédito", pct: "" }, { id: newId(), nombre: "QR", pct: "" }] };

/**
 * Procesadoras de cobro (Bancard, Dinelco, Pix...) con su % por medio (crédito, débito, QR...).
 * Se usan al cobrar una venta; la comisión no entra a la cuenta bancaria.
 */
export default function ProcesadorasCobroPanel({ API, token, logoTipo, cuentas = [], puedeGestionar = false }) {
  const [lista, setLista] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const cargar = useCallback(async () => {
    if (!logoTipo) { setLista([]); return; }
    try {
      const r = await fetch(`${API}/admin/procesadoras-cobro?logo_tipo=${encodeURIComponent(logoTipo)}&incluir_inactivas=true`, { headers });
      setLista(r.ok ? await r.json() : []);
    } catch { setLista([]); }
  }, [API, token, logoTipo]); // eslint-disable-line

  useEffect(() => { cargar(); }, [cargar]);

  const abrir = (p) => setForm(p
    ? { ...p, cuenta_id: p.cuenta_id || "", metodos: (p.metodos || []).map(m => ({ ...m, pct: String(m.pct ?? "") })) }
    : { ...VACIO, metodos: VACIO.metodos.map(m => ({ ...m, id: newId() })) });

  const setMetodo = (i, patch) => setForm(f => ({ ...f, metodos: f.metodos.map((m, k) => (k === i ? { ...m, ...patch } : m)) }));

  const guardar = async () => {
    if (!form.nombre.trim()) return toast.error("Poné el nombre de la procesadora");
    const metodos = form.metodos.filter(m => m.nombre.trim()).map(m => ({ id: m.id, nombre: m.nombre.trim(), pct: parseFloat(String(m.pct).replace(",", ".")) || 0 }));
    if (!metodos.length) return toast.error("Agregá al menos un medio (crédito, débito, QR...)");
    setSaving(true);
    try {
      const body = { nombre: form.nombre.trim(), cuenta_id: form.cuenta_id || null, activa: form.activa !== false, metodos, logo_tipo: logoTipo };
      const r = await fetch(form.id ? `${API}/admin/procesadoras-cobro/${form.id}` : `${API}/admin/procesadoras-cobro`, { method: form.id ? "PUT" : "POST", headers, body: JSON.stringify(body) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      toast.success("Procesadora guardada");
      setForm(null);
      cargar();
    } catch (e) { toast.error(e.message || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const inactivar = async (p) => {
    if (!window.confirm(`¿Inactivar ${p.nombre}? Los cobros ya registrados conservan su comisión.`)) return;
    await fetch(`${API}/admin/procesadoras-cobro/${p.id}`, { method: "DELETE", headers });
    cargar();
  };

  if (!logoTipo) return null;
  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-heading text-white text-lg flex items-center gap-2"><CreditCard className="w-5 h-5 text-amber-400" /> Procesadoras de cobro</h2>
          <p className="text-slate-500 text-xs">Bancard, Dinelco, Pix… con el % de comisión de cada medio. Al cobrar una venta se elige la procesadora y la comisión no entra a la cuenta.</p>
        </div>
        {puedeGestionar && (
          <button onClick={() => abrir(null)} className="px-3 py-2 rounded-lg bg-amber-500/90 hover:bg-amber-400 text-black text-sm font-semibold inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Nueva</button>
        )}
      </div>
      {lista.length === 0 ? (
        <div className="text-slate-500 text-sm border border-dashed border-white/10 rounded-xl p-6 text-center">Sin procesadoras. Los cobros se registran como efectivo / transferencia.</div>
      ) : (
        <div className="space-y-2">
          {lista.map(p => (
            <div key={p.id} className={`flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-xl ${p.activa === false ? "opacity-50" : ""}`}>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium">{p.nombre}{p.activa === false && <span className="ml-2 text-[10px] text-slate-400">(inactiva)</span>}</p>
                <p className="text-slate-400 text-xs">
                  {(p.metodos || []).map(m => `${m.nombre} ${Number(m.pct || 0)}%`).join(" · ")}
                  {p.cuenta_id ? ` · Acredita en: ${cuentas.find(c => c.id === p.cuenta_id)?.nombre || "—"}` : ""}
                </p>
              </div>
              {puedeGestionar && (
                <>
                  <button onClick={() => abrir(p)} className="p-2 text-slate-400 hover:text-white"><Edit2 className="w-4 h-4" /></button>
                  {p.activa !== false && <button onClick={() => inactivar(p)} className="p-2 text-slate-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {form && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-arandu-dark border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h3 className="font-heading text-white text-lg">{form.id ? "Editar procesadora" : "Nueva procesadora"}</h3>
              <button onClick={() => setForm(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-slate-400 text-xs block mb-1">Nombre *</label>
                <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej: Bancard"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500" />
              </div>
              <div>
                <label className="text-slate-400 text-xs block mb-1">Cuenta donde acredita <span className="text-slate-500">(opcional — si no, usa la cuenta que se elija al cobrar)</span></label>
                <select value={form.cuenta_id} onChange={e => setForm(f => ({ ...f, cuenta_id: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                  <option value="">— Cuenta elegida al cobrar —</option>
                  {cuentas.filter(c => c.activa !== false).map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.moneda || "PYG"})</option>)}
                </select>
              </div>
              <div>
                <label className="text-slate-400 text-xs block mb-1">Medios y % de comisión</label>
                <div className="space-y-2">
                  {form.metodos.map((m, i) => (
                    <div key={m.id} className="flex items-center gap-2">
                      <input value={m.nombre} onChange={e => setMetodo(i, { nombre: e.target.value })} placeholder="Crédito, Débito, QR, Pix…"
                        className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
                      <input type="number" min="0" step="0.01" value={m.pct} onChange={e => setMetodo(i, { pct: e.target.value })} placeholder="%"
                        className="w-24 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm text-right" />
                      <button type="button" onClick={() => setForm(f => ({ ...f, metodos: f.metodos.filter((_, k) => k !== i) }))} className="text-slate-500 hover:text-red-400"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={() => setForm(f => ({ ...f, metodos: [...f.metodos, { id: newId(), nombre: "", pct: "" }] }))}
                  className="mt-2 text-xs text-amber-300 hover:text-amber-200 underline">+ Agregar medio</button>
              </div>
              <label className="flex items-center gap-2 text-slate-300 text-sm cursor-pointer">
                <input type="checkbox" checked={form.activa !== false} onChange={e => setForm(f => ({ ...f, activa: e.target.checked }))} className="accent-amber-500" /> Activa
              </label>
            </div>
            <div className="p-5 border-t border-white/10 flex justify-end gap-2">
              <button onClick={() => setForm(null)} className="px-4 py-2 rounded-lg text-slate-300 hover:bg-white/5 text-sm">Cancelar</button>
              <button onClick={guardar} disabled={saving} className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50"><Check className="w-4 h-4" /> Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
